/**
 * voice_room_rules.ts — VOICE ROOMS, the pure rules (2026-09-24). "Implement voice rooms with an SFU."
 *
 * BYTE-IDENTICAL at server/src/lib/voice_room_rules.ts and client/src/lib/voice_room_rules.ts (verify:voice-rooms
 * diffs them); the SFU worker (server/workers/voice-sfu) carries its own copy of the few numbers it needs and the
 * harness pins them against these. No imports.
 *
 * THE SHAPE (the research: Discord's voice channels and stage channels, WoW's party / raid / guild voice, the SFU
 * model — every speaker's audio forwarded, never mixed, so each listener sets each voice's volume):
 *   - A ROOM is a team's (party, guild, city — joined by membership) or an OPEN room a player made (public, or invite
 *     only). Its MODE is `open` (everyone speaks) or `stage` (speakers speak; the audience listens and raises a hand).
 *   - A SEAT (`voice_room_member`) is taken by a reducer that checks eligibility, bans and capacity; the SFU admits a
 *     client only with a one-use TICKET that reducer issued. One room at a time.
 *   - A moderator's mute and a stage listener's silence are enforced at the SFU (the producer is paused there), not
 *     trusted to the client. A BLOCK between two people means neither receives the other's audio — the SFU never
 *     makes that consumer.
 */

export const VOICE_KINDS = ['party', 'guild', 'city', 'open'] as const;
export type VoiceKind = (typeof VOICE_KINDS)[number];
export const VOICE_ACCESS = ['public', 'invite', 'members'] as const;
export type VoiceAccess = (typeof VOICE_ACCESS)[number];
export const VOICE_MODES = ['open', 'stage'] as const;
export type VoiceMode = (typeof VOICE_MODES)[number];
export const VOICE_ROLES = ['owner', 'mod', 'speaker', 'listener'] as const;
export type VoiceRole = (typeof VOICE_ROLES)[number];

/** Seats by kind (a mediasoup worker carries ~500 consumers; an audio room of n people is n × (n − 1)). */
export const VOICE_CAPACITY: Record<VoiceKind, number> = { party: 8, guild: 50, city: 50, open: 25 };
export const VOICE_CAPACITY_MAX = 50;
export const VOICE_CAPACITY_MIN = 2;

/** A ticket lives a minute; a seat whose audio never connects (or drops) is freed after the grace. */
export const VOICE_TICKET_TTL_S = 60;
export const VOICE_SEAT_GRACE_S = 45;
/** No heartbeat from the SFU for this long and every seat is marked disconnected. */
export const VOICE_SFU_STALE_S = 30;
/** A room nobody sits in closes after ten minutes. */
export const VOICE_EMPTY_CLOSE_S = 600;
/** A traveler may own this many open rooms at once. */
export const VOICE_OPEN_ROOMS_PER_OWNER = 3;
export const VOICE_NAME_MAX = 40;
export const VOICE_TOPIC_MAX = 80;
/** A kick's ban, at most a day. */
export const VOICE_BAN_MAX_MIN = 24 * 60;

/** The role a traveler takes on sitting down. */
export function defaultVoiceRole(mode: string, isOwner: boolean, isTeamLead: boolean): VoiceRole {
  if (isOwner) return 'owner';
  if (isTeamLead) return 'mod';
  return mode === 'stage' ? 'listener' : 'speaker';
}

/** Whether a role may speak in a mode. */
export function voiceCanSpeak(mode: string, role: string): boolean {
  if (mode === 'stage') return role === 'owner' || role === 'mod' || role === 'speaker';
  return role !== 'listener';
}

export function voiceCanModerate(role: string): boolean { return role === 'owner' || role === 'mod'; }

/**
 * Whether `actor` may move `target` to `next`. The owner can do anything to anyone else; a mod moves speakers and
 * listeners between each other but never touches the owner or another mod, and never makes a mod. Nobody changes
 * their own role (a listener asks by raising a hand).
 */
export function voiceMaySetRole(actor: string, target: string, next: string, self: boolean): boolean {
  if (self) return false;
  if (!(VOICE_ROLES as readonly string[]).includes(next) || next === 'owner') return false;
  if (actor === 'owner') return target !== 'owner';
  if (actor === 'mod') return (target === 'speaker' || target === 'listener') && (next === 'speaker' || next === 'listener');
  return false;
}

/** Whether a moderator may mute or kick a target. */
export function voiceMayDiscipline(actor: string, target: string, self: boolean): boolean {
  if (self) return false;
  if (actor === 'owner') return target !== 'owner';
  if (actor === 'mod') return target === 'speaker' || target === 'listener';
  return false;
}

/** Whether the microphone is open: may speak, not held silent by a mod, not self-muted, and (in PTT) the key held. */
export function voiceMicOpen(o: { canSpeak: boolean; serverMuted: boolean; selfMuted: boolean; pushToTalk: boolean; pttHeld: boolean }): boolean {
  if (!o.canSpeak || o.serverMuted || o.selfMuted) return false;
  return o.pushToTalk ? o.pttHeld : true;
}

/**
 * SPEAKING, as a ring should show it: the SFU reports each producer's level (dBov, −127..0 — RFC 6464, read from the
 * RTP header extension, never decoded). Loud above −55 dBov; the ring holds 450 ms after the last loud report so it
 * does not flicker between words.
 */
export const VOICE_SPEAKING_DB = -55;
export const VOICE_SPEAKING_HOLD_MS = 450;
export function voiceSpeaking(lastLoudMs: number, nowMs: number): boolean {
  return nowMs - lastLoudMs <= VOICE_SPEAKING_HOLD_MS;
}
export function voiceLoud(dBov: number): boolean { return dBov > VOICE_SPEAKING_DB; }

/** The roster's order: owner, mods, speakers, listeners (raised hands first), then by name. */
export function voiceRosterOrder(a: { role: string; handRaised: boolean; name: string }, b: { role: string; handRaised: boolean; name: string }): number {
  const rank = (r: string) => Math.max(0, (VOICE_ROLES as readonly string[]).indexOf(r));
  if (rank(a.role) !== rank(b.role)) return rank(a.role) - rank(b.role);
  if (a.handRaised !== b.handRaised) return a.handRaised ? -1 : 1;
  return a.name.localeCompare(b.name);
}

/** A room's name: trimmed, control bytes gone, at most VOICE_NAME_MAX. */
export function cleanVoiceName(s: string, max = VOICE_NAME_MAX): string {
  let out = '';
  for (const ch of s) {
    const c = ch.codePointAt(0) ?? 0;
    if (c < 0x20 || (c >= 0x7f && c < 0xa0)) continue;
    out += ch;
  }
  return out.trim().replace(/\s+/g, ' ').slice(0, max);
}

/**
 * Where the SFU's signaling lives, as seen from a page: an override wins; an https page uses its own origin's
 * `/voice-sfu` path (the front proxy — a secure page may only open wss); a plain-http page (local development) talks
 * to the worker directly on 7880.
 */
export const VOICE_SFU_PORT = 7880;
export const VOICE_SFU_PATH = '/voice';
export function voiceSfuUrl(loc: { protocol: string; hostname: string; host: string }, override: string): string {
  if (override) return override;
  if (loc.protocol === 'https:') return `wss://${loc.host}/voice-sfu${VOICE_SFU_PATH}`;
  return `ws://${loc.hostname || 'localhost'}:${VOICE_SFU_PORT}${VOICE_SFU_PATH}`;
}

/** Opus for voice: mono, in-band FEC for loss, DTX for silence, 32 kbit/s — the WebRTC voice defaults, stated. */
export const VOICE_OPUS = { stereo: false, fec: true, dtx: true, maxAverageBitrate: 32_000, ptime: 20 } as const;
